<template>
  <div v-if="visible" class="vl-overlay" @click.self="close">
    <div class="vl-modal" role="dialog" aria-modal="true" aria-labelledby="vl-title">
      <div class="vl-header">
        <div class="vl-header__main">
          <div class="vl-header__left">
            <h2 id="vl-title">Клиент по коду</h2>
            <div class="vl-code-line">
              <span class="vl-code">{{ normalizedCode }}</span>
              <span
                v-if="result"
                class="vl-badge"
                :class="result.online ? 'vl-badge--online' : 'vl-badge--offline'"
              >
                {{ result.online ? 'Онлайн сейчас' : (result.live ? 'Был на сайте' : 'Нет live-сессии') }}
              </span>
              <span v-if="seenLabel" class="vl-seen">{{ seenLabel }}</span>
            </div>
          </div>
          <div v-if="cars.length" class="vl-cars" title="Автомобили из подбора">
            <div v-for="(car, idx) in cars" :key="car.label + '-' + idx" class="vl-car">
              {{ car.label }}
            </div>
          </div>
          <button
            type="button"
            class="vl-save-code"
            :class="{ 'vl-save-code--saved': isAlreadySaved }"
            :disabled="!canSaveCode || saving"
            @click="saveCode"
          >
            {{ saveButtonLabel }}
          </button>
        </div>
        <button type="button" class="vl-close" @click="close" aria-label="Закрыть">×</button>
      </div>

      <div class="vl-body">
        <div v-if="loading" class="vl-loading">Ищем клиента…</div>
        <div v-else-if="error" class="vl-error">{{ error }}</div>
        <div v-else-if="result && !result.found" class="vl-empty">
          По коду <b>{{ normalizedCode }}</b> пока нет данных.
          <div class="vl-hint">Клиент должен быть на сайте (код в шапке) — сессия появится в течение нескольких секунд.</div>
        </div>

        <template v-else-if="result">
          <div class="vl-meta">
            <div class="vl-meta__item">
              <span class="vl-meta__label">Город</span>
              <span class="vl-meta__value">{{ result.city || '—' }}</span>
            </div>
            <div class="vl-meta__item" v-if="offerDiscount != null">
              <span class="vl-meta__label">Товар дня</span>
              <span class="vl-meta__value vl-meta__value--accent">−{{ offerDiscount }}%</span>
            </div>
            <div class="vl-meta__item" v-if="liveHost">
              <span class="vl-meta__label">Хост</span>
              <span class="vl-meta__value">{{ liveHost }}</span>
            </div>
          </div>

          <div v-if="dateKeys.length" class="vl-dates">
            <button
              v-for="d in dateKeys"
              :key="d"
              type="button"
              class="vl-date"
              :class="{ 'vl-date--active': d === selectedDate }"
              @click="selectedDate = d"
            >
              {{ formatDayLabel(d) }}
              <span class="vl-date__n">{{ (visitsByDate[d] || []).length }}</span>
            </button>
          </div>

          <div v-if="offerProduct" class="vl-section">
            <h3>Активное предложение</h3>
            <a
              class="vl-offer"
              :href="offerProduct.url || '#'"
              target="_blank"
              rel="noopener"
            >
              <img v-if="offerProduct.img" :src="offerProduct.img" alt="" class="vl-offer__img" />
              <div class="vl-offer__info">
                <div class="vl-offer__name">{{ offerProduct.name }}</div>
                <div class="vl-offer__price">
                  <span v-if="offerProduct.price">{{ offerProduct.price }} руб.</span>
                  <span v-if="offerDiscount != null" class="vl-offer__disc">−{{ offerDiscount }}%</span>
                </div>
              </div>
            </a>
          </div>

          <div v-if="dayProducts.length" class="vl-section">
            <h3>Смотрел АКБ <span class="vl-count">{{ dayProducts.length }}</span></h3>
            <div class="vl-akb">
              <a
                v-for="p in dayProducts"
                :key="p.id || productHref(p)"
                class="vl-akb__item"
                :href="productHref(p)"
                target="_blank"
                rel="noopener"
                :title="p.name || ''"
              >
                <img
                  :src="productImg(p)"
                  :alt="p.name || 'АКБ'"
                  loading="lazy"
                />
                <div class="vl-akb__name">{{ p.name || 'АКБ' }}</div>
                <div v-if="p.price" class="vl-akb__price">{{ formatPrice(p.price) }} руб.</div>
              </a>
            </div>
          </div>

          <div v-if="dayPages.length" class="vl-section">
            <h3>
              Страницы
              <span v-if="selectedDate" class="vl-section__sub">{{ formatDayLabel(selectedDate) }}</span>
              <span class="vl-count">{{ dayPages.length }}</span>
            </h3>
            <ul class="vl-pages">
              <li v-for="(page, idx) in dayPagesReversed" :key="(page.url || page.path) + '-' + idx">
                <a :href="page.url || '#'" target="_blank" rel="noopener">
                  {{ page.h1 || page.title || page.path || page.url }}
                </a>
                <span v-if="page.path" class="vl-pages__path">{{ page.path }}</span>
              </li>
            </ul>
          </div>

          <div v-if="orders.length" class="vl-section">
            <h3>Заказы с этим кодом <span class="vl-count">{{ orders.length }}</span></h3>
            <div class="vl-orders">
              <div v-for="o in orders" :key="o.id || o.number" class="vl-order">
                <div class="vl-order__top">
                  <router-link
                    v-if="o.id || o.slug"
                    :to="'/order/' + (o.slug || o.id)"
                    class="vl-order__link"
                    @click.native="close"
                  >
                    № {{ o.number || o.id }}
                  </router-link>
                  <span v-else>№ {{ o.number || o.id }}</span>
                  <span class="vl-order__date">{{ formatDate(o.createdAt) }}</span>
                </div>
                <div class="vl-order__row">
                  <span>{{ [o.lastName, o.firstName].filter(Boolean).join(' ') || '—' }}</span>
                  <span v-if="o.phone">{{ o.phone }}</span>
                  <span v-if="o.totalSumm != null">{{ o.totalSumm }} руб.</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="vl-footer">
        <button type="button" class="vl-btn vl-btn--ghost" @click="refresh" :disabled="loading">Обновить</button>
        <button type="button" class="vl-btn" @click="close">Закрыть</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ORDERS_API_BASE } from '@/config/api';

const CODE_RE = /^\d{3}[ABCDKLMFE]\d$/i;

function normalizeUrlKey(url) {
  if (!url) return '';
  try {
    const u = new URL(String(url), 'https://1akb.by');
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch (e) {
    return String(url).replace(/\/+$/, '').toLowerCase();
  }
}

export default {
  name: 'VisitorLookupModal',
  props: {
    visible: { type: Boolean, default: false },
    code: { type: String, default: '' },
    savedCode: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      error: '',
      result: null,
      selectedDate: '',
      saving: false,
    };
  },
  computed: {
    normalizedCode() {
      return String(this.code || '').trim().toUpperCase();
    },
    normalizedSavedCode() {
      return String(this.savedCode || '').trim().toUpperCase();
    },
    canSaveCode() {
      return CODE_RE.test(this.normalizedCode);
    },
    isAlreadySaved() {
      return this.canSaveCode
        && this.normalizedSavedCode
        && this.normalizedSavedCode === this.normalizedCode;
    },
    saveButtonLabel() {
      if (this.saving) return 'Сохраняем…';
      if (this.isAlreadySaved) return 'Код в заказе';
      return 'Сохранить код';
    },
    live() {
      return (this.result && this.result.live) || null;
    },
    liveHost() {
      return this.live && this.live.host ? this.live.host : '';
    },
    visitsByDate() {
      const fromLive = this.live && this.live.visitsByDate && typeof this.live.visitsByDate === 'object'
        ? this.live.visitsByDate
        : null;
      if (fromLive && Object.keys(fromLive).length) {
        return fromLive;
      }
      // fallback: одна «корзина» из pages
      const pages = (this.live && Array.isArray(this.live.pages)) ? this.live.pages : [];
      if (!pages.length) return {};
      const today = this.todayKey();
      return { [today]: pages };
    },
    dateKeys() {
      return Object.keys(this.visitsByDate).sort().reverse();
    },
    dayPages() {
      if (!this.selectedDate) return [];
      const list = this.visitsByDate[this.selectedDate];
      return Array.isArray(list) ? list : [];
    },
    dayPagesReversed() {
      return this.dayPages.slice().reverse();
    },
    dayUrlKeys() {
      const keys = new Set();
      this.dayPages.forEach((p) => {
        const k = normalizeUrlKey(p && p.url);
        if (k) keys.add(k);
      });
      return keys;
    },
    products() {
      return (this.result && Array.isArray(this.result.products)) ? this.result.products : [];
    },
    dayProducts() {
      if (!this.products.length) return [];
      // Если нет привязки по дате — показываем все
      if (!this.dayUrlKeys.size) return this.products;
      const filtered = this.products.filter((p) => {
        const keys = [
          normalizeUrlKey(p.visitedUrl),
          normalizeUrlKey(p.href),
          normalizeUrlKey(p.url),
          normalizeUrlKey(p.detailUrl),
        ].filter(Boolean);
        return keys.some((k) => this.dayUrlKeys.has(k));
      });
      return filtered.length ? filtered : this.products;
    },
    cars() {
      const list = (this.live && Array.isArray(this.live.cars)) ? this.live.cars : [];
      const normBrand = (b) => String(b || '').toLowerCase().replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
      const modelFamily = (m) => String(m || '').toLowerCase()
        .replace(/\s*\([^)]*\)\s*/g, ' ')
        .replace(/\d{4}\s*[-–—]\s*\d{4}/g, ' ')
        .replace(/\s+(?:[ivxlcdm]+|\d+)\s*$/i, '')
        .replace(/\s+/g, ' ')
        .trim();
      const specificity = (c) => {
        let s = 0;
        if (String(c.generationName || '').trim()) s += 4;
        if (String(c.engineName || '').trim()) s += 2;
        s += Math.min(String(c.label || '').length, 300) / 100;
        s += (Number(c.timestamp) || 0) / 1e15;
        return s;
      };
      const byFamily = new Map();
      list
        .filter((c) => c && String(c.modelName || '').trim())
        .map((c) => ({
          ...c,
          label: c.label || [c.brandName, c.modelName, c.generationName, c.engineName].filter(Boolean).join(' '),
        }))
        .filter((c) => c.label)
        .forEach((c) => {
          const key = `${normBrand(c.brandName)}|${modelFamily(c.modelName)}`;
          const prev = byFamily.get(key);
          if (!prev || specificity(c) >= specificity(prev)) {
            byFamily.set(key, c);
          }
        });
      return Array.from(byFamily.values())
        .sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0))
        .reverse();
    },
    orders() {
      return (this.result && Array.isArray(this.result.orders)) ? this.result.orders : [];
    },
    offerProduct() {
      return this.live && this.live.offer && this.live.offer.product
        ? this.live.offer.product
        : null;
    },
    offerDiscount() {
      if (this.live && this.live.offer && this.live.offer.discount != null) {
        return this.live.offer.discount;
      }
      if (this.orders.length && this.orders[0].personal_offer_discount != null) {
        return this.orders[0].personal_offer_discount;
      }
      return null;
    },
    seenLabel() {
      if (!this.result || this.result.secondsAgo == null) return '';
      const s = Number(this.result.secondsAgo);
      if (s < 60) return `${s} сек. назад`;
      if (s < 3600) return `${Math.floor(s / 60)} мин. назад`;
      if (s < 86400) return `${Math.floor(s / 3600)} ч. назад`;
      return `${Math.floor(s / 86400)} дн. назад`;
    },
  },
  watch: {
    visible(val) {
      if (val) this.fetchLookup();
    },
    code() {
      if (this.visible) this.fetchLookup();
    },
    dateKeys(keys) {
      if (!keys.length) {
        this.selectedDate = '';
        return;
      }
      if (!keys.includes(this.selectedDate)) {
        this.selectedDate = keys[0];
      }
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    refresh() {
      this.fetchLookup();
    },
    saveCode() {
      if (!this.canSaveCode || this.saving) return;
      this.saving = true;
      const discount = this.offerDiscount;
      this.$emit('save-code', {
        code: this.normalizedCode,
        city: (this.result && this.result.city) || '',
        discount: discount != null ? discount : null,
      });
      // Parent closes modal / toasts; reset local flag shortly
      window.setTimeout(() => {
        this.saving = false;
      }, 1500);
    },
    todayKey() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDayLabel(iso) {
      if (!iso) return '';
      const parts = String(iso).split('-');
      if (parts.length !== 3) return iso;
      const today = this.todayKey();
      if (iso === today) return 'Сегодня';
      const yest = new Date();
      yest.setDate(yest.getDate() - 1);
      const y = yest.getFullYear();
      const m = String(yest.getMonth() + 1).padStart(2, '0');
      const d = String(yest.getDate()).padStart(2, '0');
      if (iso === `${y}-${m}-${d}`) return 'Вчера';
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    },
    formatDate(val) {
      if (!val) return '';
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleString('ru-RU');
      } catch (e) {
        return String(val);
      }
    },
    formatPrice(val) {
      const n = Number(val);
      if (!isFinite(n)) return val;
      return Math.round(n);
    },
    productImg(p) {
      return (p && (p.jpg_url || p.picture || p.img || p.image)) || 'https://1akb.by/images/no-image_0.webp';
    },
    productHref(p) {
      return (p && (p.href || p.url || p.detailUrl || p.visitedUrl)) || '#';
    },
    async fetchLookup() {
      const code = this.normalizedCode;
      if (!CODE_RE.test(code)) {
        this.error = 'Код должен быть вида 747A1 (3 цифры, буква, цифра)';
        this.result = null;
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(`${ORDERS_API_BASE}/visitor-lookup.php`, {
          params: { code },
          timeout: 20000,
        });
        if (!data || data.success === false) {
          this.error = (data && data.error) || 'Не удалось получить данные';
          this.result = null;
          return;
        }
        this.result = data;
        this.$nextTick(() => {
          if (this.dateKeys.length && !this.dateKeys.includes(this.selectedDate)) {
            this.selectedDate = this.dateKeys[0];
          }
        });
      } catch (e) {
        this.error = (e && e.message) || 'Ошибка запроса';
        this.result = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.vl-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px 24px;
  overflow-y: auto;
}
.vl-modal {
  width: 100%;
  max-width: 780px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 72px);
}
.vl-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px 14px 20px;
  border-bottom: 1px solid #e8edf3;
}
.vl-header__main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.vl-header__left {
  min-width: 0;
  flex: 0 1 auto;
}
.vl-header h2 {
  margin: 0 0 4px;
  font-size: 18px;
  line-height: 1.25;
  color: #0f172a;
}
.vl-code-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.vl-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.04em;
  color: #0b5fff;
  line-height: 1.2;
}
.vl-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  line-height: 1.3;
}
.vl-badge--online {
  background: #dcfce7;
  color: #166534;
}
.vl-badge--offline {
  background: #f1f5f9;
  color: #475569;
}
.vl-seen {
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
}
.vl-cars {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  margin-left: auto;
  max-width: min(320px, 42%);
  min-width: 0;
}
.vl-car {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 6px 10px;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vl-save-code {
  border: none;
  background: #0b5fff;
  color: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  height: 34px;
  line-height: 1;
}
.vl-save-code:disabled {
  opacity: 0.55;
  cursor: default;
}
.vl-save-code--saved {
  background: #16a34a;
}
.vl-close {
  border: none;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
  padding: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.vl-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
}
.vl-loading,
.vl-error,
.vl-empty {
  padding: 24px 8px;
  text-align: center;
  color: #334155;
}
.vl-error { color: #b91c1c; }
.vl-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
}
.vl-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.vl-meta__item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}
.vl-meta__label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 2px;
}
.vl-meta__value {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.vl-meta__value--accent { color: #c2410c; }
.vl-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}
.vl-date {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.vl-date--active {
  background: #0b5fff;
  border-color: #0b5fff;
  color: #fff;
}
.vl-date__n {
  font-size: 10px;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  padding: 1px 6px;
}
.vl-date--active .vl-date__n {
  background: rgba(255, 255, 255, 0.22);
}
.vl-section { margin-bottom: 18px; }
.vl-section h3 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #0f172a;
}
.vl-section__sub {
  margin-left: 6px;
  font-weight: 500;
  color: #64748b;
  font-size: 12px;
}
.vl-count {
  display: inline-block;
  margin-left: 4px;
  font-size: 11px;
  background: #e2e8f0;
  color: #334155;
  border-radius: 999px;
  padding: 1px 7px;
}
.vl-offer {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px;
  border: 1px solid #fed7aa;
  background: #fff7ed;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
}
.vl-offer__img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  background: #fff;
  border-radius: 6px;
}
.vl-offer__name { font-weight: 600; font-size: 13px; }
.vl-offer__price { font-size: 12px; color: #9a3412; margin-top: 4px; }
.vl-offer__disc { margin-left: 6px; font-weight: 700; }
.vl-akb {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}
.vl-akb__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  text-decoration: none;
  color: inherit;
  padding: 10px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fafbfc;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.vl-akb__item:hover {
  border-color: #93c5fd;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}
.vl-akb__item img {
  width: 100%;
  height: 96px;
  object-fit: contain;
  margin-bottom: 8px;
  background: #fff;
  border-radius: 6px;
}
.vl-akb__name {
  font-size: 11px;
  line-height: 1.3;
  color: #0f172a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
}
.vl-akb__price {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #0b5fff;
}
.vl-pages {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.vl-pages li {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
}
.vl-pages li:last-child { border-bottom: none; }
.vl-pages a {
  color: #0b5fff;
  text-decoration: none;
  font-size: 13px;
}
.vl-pages__path {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}
.vl-orders { display: flex; flex-direction: column; gap: 8px; }
.vl-order {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}
.vl-order__top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.vl-order__link {
  font-weight: 600;
  color: #0b5fff;
  text-decoration: none;
}
.vl-order__date { font-size: 12px; color: #64748b; }
.vl-order__row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: #334155;
}
.vl-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid #e8edf3;
}
.vl-btn {
  border: none;
  background: #0b5fff;
  color: #fff;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
}
.vl-btn:disabled { opacity: 0.6; cursor: default; }
.vl-btn--ghost {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
}
@media (max-width: 640px) {
  .vl-meta { grid-template-columns: 1fr; }
  .vl-header {
    align-items: flex-start;
  }
  .vl-header__main {
    flex-wrap: wrap;
  }
  .vl-cars {
    order: 3;
    flex: 1 1 100%;
    max-width: none;
    margin-left: 0;
    align-items: flex-start;
  }
  .vl-save-code {
    margin-left: auto;
  }
}
</style>
